import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getChatHistory } from '../services/chatService';
import { MessageActionHandler } from '../utils/messageActionHandler';
import { useAuth } from './AuthContext';

const ChatPanelContext = createContext(null);

export const useChatPanel = () => {
  const context = useContext(ChatPanelContext);
  if (context === undefined) {
    throw new Error('useChatPanel must be used within a ChatPanelProvider');
  }
  return context;
};

/**
 * IMPROVED: Enhanced ChatPanelContext as single source of truth
 * Manages all conversation state, loading, and initialization
 */
export const ChatPanelProvider = ({ children }) => {
  const { state: authState } = useAuth();
  const prevAuthStateRef = useRef();
  const [chatPanelState, setChatPanelState] = useState({
    isVisible: false,
    isMinimized: false,
    conversation: [],
    threadId: null,
    sourceRoute: null,
    currentParams: null,
    width: 400,
    loading: false,
    isAiThinking: false, // Combined loading state for field help and chat
    isInitialized: false,
    visitorTokenLimitReached: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const mapCheckpointMessages = useCallback((checkpointMessages = []) => {
    return checkpointMessages.map(msg => ({
      id: msg.id || null,
      type: msg.role === 'user' ? 'user' : 'ai',
      text: MessageActionHandler.processMessageAction(
        msg.content,
        msg.structured_response?.action
      ),
      message_type: msg.message_type || '',
      action: msg.structured_response?.action,
      visualizations: Array.isArray(msg.structured_response?.visualizations)
        ? msg.structured_response.visualizations
        : [],
      feedback_value: msg.feedback_value || null,
    }));
  }, []);

  // Initialize chat history only after auth is ready
  useEffect(() => {
    // Wait for auth to be initialized and don't initialize chat twice
    if (!authState.isInitialized || chatPanelState.isInitialized) {
      // console.log('ChatPanelProvider: Skipping initialization - authInitialized:', authState.isInitialized, 'chatInitialized:', chatPanelState.isInitialized);
      return;
    }

    const controller = new AbortController();
    // console.log('ChatPanelProvider: Starting chat history initialization - authenticated:', authState.isAuthenticated);

    const initializeChatHistory = async () => {
      try {
        setChatPanelState(prev => ({ ...prev, loading: true }));

        // Always fetch chat history once auth is initialized.
        // For authenticated users this returns user-thread history;
        // for unauthenticated users it returns visitor-thread history via visitor_id cookie.
        const history = await getChatHistory({ signal: controller.signal });

        if (controller.signal.aborted) return;

        // Handle both new API response format and legacy format
        const historyData = history?.data || history;
        const checkpoints = historyData?.checkpoints || [];
        const tokenLimitReached = historyData?.visitor_token_info?.limit_reached === true;

        if (checkpoints.length > 0) {
          const checkpoint = checkpoints[checkpoints.length - 1];
          if (checkpoint?.messages) {
            const formattedMessages = mapCheckpointMessages(checkpoint.messages);

            // Update ref to prevent double fetch in the auth change effect
            prevAuthStateRef.current = authState;

            setChatPanelState(prev => ({
              ...prev,
              conversation: formattedMessages,
              threadId: historyData?.thread_id || prev.threadId,
              loading: false,
              isInitialized: true,
              visitorTokenLimitReached: tokenLimitReached,
            }));
            return;
          }
        }
        console.log('ChatPanelProvider: No chat history found during initialization');

        if (controller.signal.aborted) return;

        // Update ref to prevent double fetch in the auth change effect
        prevAuthStateRef.current = authState;

        // If not authenticated or no chat history, just mark as initialized
        setChatPanelState(prev => ({
          ...prev,
          loading: false,
          isInitialized: true,
          visitorTokenLimitReached: tokenLimitReached,
        }));
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('ChatPanelProvider: Chat history fetch aborted');
          return;
        }
        console.error('ChatPanelProvider: Failed to initialize chat history:', error);
        setChatPanelState(prev => ({
          ...prev,
          loading: false,
          isInitialized: true
        }));
      }
    };

    initializeChatHistory();

    return () => {
      controller.abort();
    };
  }, [authState.isInitialized, authState.isAuthenticated, chatPanelState.isInitialized, mapCheckpointMessages]);

  // Handle authentication state changes (login/logout)
  useEffect(() => {
    // Skip if not initialized yet
    if (!authState.isInitialized || !chatPanelState.isInitialized) {
      return;
    }

    // Check if authentication state actually changed
    const prevAuthState = prevAuthStateRef.current;
    if (prevAuthState && prevAuthState.isAuthenticated === authState.isAuthenticated) {
      return; // No change in auth state
    }

    // Update ref to current state
    prevAuthStateRef.current = authState;

    const controller = new AbortController();
    // console.log('ChatPanelProvider: Authentication state changed, reloading conversation - authenticated:', authState.isAuthenticated);

    const reloadConversation = async () => {
      try {
        setChatPanelState(prev => ({ ...prev, loading: true }));
        
        if (authState.isAuthenticated) {
          // console.log('ChatPanelProvider: User logged in, fetching authenticated conversation');
          const history = await getChatHistory({ signal: controller.signal });
          
          if (controller.signal.aborted) return;

          // Handle both new API response format and legacy format
          const historyData = history?.data || history;
          const checkpoints = historyData?.checkpoints || [];
          
          if (checkpoints.length > 0) {
            const checkpoint = checkpoints[checkpoints.length - 1];
            if (checkpoint?.messages) {
              const formattedMessages = mapCheckpointMessages(checkpoint.messages);

              // console.log('ChatPanelProvider: Reloaded', formattedMessages.length, 'authenticated messages');
              setChatPanelState(prev => ({
                ...prev,
                conversation: formattedMessages,
                threadId: historyData?.thread_id || prev.threadId,
                loading: false
              }));
              return;
            }
          }
          // console.log('ChatPanelProvider: No authenticated conversation found, clearing chat');
          setChatPanelState(prev => ({
            ...prev,
            conversation: [],
            loading: false
          }));
        } else {
          // console.log('ChatPanelProvider: User logged out, fetching visitor conversation');
          // User logged out, fetch visitor conversation
          const history = await getChatHistory({ signal: controller.signal });
          
          if (controller.signal.aborted) return;

          // Handle both new API response format and legacy format
          const historyData = history?.data || history;
          const checkpoints = historyData?.checkpoints || [];
          
          if (checkpoints.length > 0) {
            const checkpoint = checkpoints[checkpoints.length - 1];
            if (checkpoint?.messages) {
              const formattedMessages = mapCheckpointMessages(checkpoint.messages);

              console.log('ChatPanelProvider: Reloaded', formattedMessages.length, 'visitor messages');
              setChatPanelState(prev => ({
                ...prev,
                conversation: formattedMessages,
                threadId: historyData?.thread_id || prev.threadId,
                loading: false
              }));
              return;
            }
          }
          // console.log('ChatPanelProvider: No visitor conversation found, clearing chat');
          setChatPanelState(prev => ({
            ...prev,
            conversation: [],
            loading: false
          }));
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        // console.error('ChatPanelProvider: Failed to reload conversation after auth change:', error);
        setChatPanelState(prev => ({
          ...prev,
          loading: false
        }));
      }
    };

    reloadConversation();

    return () => controller.abort();
  }, [authState.isAuthenticated, authState.isInitialized, chatPanelState.isInitialized, mapCheckpointMessages]); // Include isInitialized dependencies

  // Show chat panel (when coming from action or manual activation)
  const showChatPanel = useCallback((sourceRoute = null, params = null, options = {}) => {
    const { startMinimized = false } = options;

    setChatPanelState(prev => ({
      ...prev,
      isVisible: true,
      isMinimized: startMinimized,
      sourceRoute: sourceRoute || location.pathname,
      currentParams: params,
    }));
  }, [location.pathname]);

  // Hide chat panel completely
  const hideChatPanel = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      isVisible: false,
      isMinimized: false,
      sourceRoute: null,
      currentParams: null,
    }));
  }, []);

  // Toggle minimized state
  const toggleMinimizeChat = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      isMinimized: !prev.isMinimized,
    }));
  }, []);

  // Restore chat panel from minimized state
  const restoreChatPanel = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      isVisible: true,
      isMinimized: false,
    }));
  }, []);

  // Navigate with chat context (for actions)
  const navigateWithChat = useCallback((targetPath, params = null) => {
    // Full URLs (http/https) open in a new tab as-is
    if (targetPath.startsWith('http://') || targetPath.startsWith('https://')) {
      window.open(targetPath, '_blank', 'noopener,noreferrer');
      return;
    }

    // Relative paths navigate in the same tab
    showChatPanel(location.pathname, params);
    
    // Build URL with params
    const url = params ? `${targetPath}?${params}` : targetPath;
    
    // Navigate to target
    navigate(url, { 
      state: { 
        showChat: true, 
        sourceRoute: location.pathname,
        chatParams: params 
      } 
    });
  }, [navigate, location.pathname, showChatPanel]);

  // Go back to chat-only view
  const returnToChat = useCallback(() => {
    navigate('/chat', { 
      state: { 
        fromMultiModal: true,
        preserveConversation: true 
      } 
    });
    hideChatPanel();
  }, [navigate, hideChatPanel]);

  // Update conversation
  const updateConversation = useCallback((newConversationOrUpdater) => {
    setChatPanelState(prev => {
      const prevConversation = Array.isArray(prev.conversation) ? prev.conversation : [];
      const newConversation = typeof newConversationOrUpdater === 'function' 
        ? newConversationOrUpdater(prevConversation)
        : newConversationOrUpdater;
      
      // Ensure the new conversation is always an array
      const safeNewConversation = Array.isArray(newConversation) ? newConversation : [];
      
      // console.log('🔄 ChatPanelContext updateConversation called:');
      // console.log('  - Previous conversation length:', prevConversation.length);
      // console.log('  - New conversation length:', safeNewConversation.length);
      // console.log('  - Is function updater:', typeof newConversationOrUpdater === 'function');
      
      return {
        ...prev,
        conversation: safeNewConversation,
      };
    });
  }, []);

  // Set loading state
  const setLoading = useCallback((loading) => {
    setChatPanelState(prev => ({
      ...prev,
      loading
    }));
  }, []);

  // Set AI thinking state (for field help and chat loading)
  const setAiThinking = useCallback((isAiThinking) => {
    setChatPanelState(prev => ({
      ...prev,
      isAiThinking
    }));
  }, []);

  // Mark visitor as having reached the token limit
  const setVisitorTokenLimitReached = useCallback((reached) => {
    setChatPanelState(prev => ({
      ...prev,
      visitorTokenLimitReached: reached,
    }));
  }, []);

  // Clear conversation
  const clearConversation = useCallback(() => {
    setChatPanelState(prev => ({
      ...prev,
      conversation: []
    }));
  }, []);

  // Resize chat panel
  const resizeChatPanel = useCallback((newWidth) => {
    setChatPanelState(prev => ({
      ...prev,
      width: Math.max(300, Math.min(600, newWidth)), // Min 300px, max 600px
    }));
  }, []);

  // Check if current route should show chat panel
  const shouldShowChatPanel = useCallback(() => {
    const state = location.state;
    return chatPanelState.isVisible || state?.showChat;
  }, [chatPanelState.isVisible, location.state]);

  // Refresh conversation manually (useful for testing or after auth changes)
  const refreshConversation = useCallback(async () => {
    if (!authState.isInitialized) {
      console.log('ChatPanelProvider: Cannot refresh - auth not initialized');
      return;
    }

    console.log('ChatPanelProvider: Manually refreshing conversation - authenticated:', authState.isAuthenticated);

    try {
      setChatPanelState(prev => ({ ...prev, loading: true }));
      
      const history = await getChatHistory();
      
      // Handle both new API response format and legacy format
      const historyData = history?.data || history;
      const checkpoints = historyData?.checkpoints || [];
      
      if (checkpoints.length > 0) {
        const checkpoint = checkpoints[checkpoints.length - 1];
        if (checkpoint?.messages) {
          const formattedMessages = mapCheckpointMessages(checkpoint.messages);

          console.log('ChatPanelProvider: Refreshed with', formattedMessages.length, 'messages');
          setChatPanelState(prev => ({
            ...prev,
            conversation: formattedMessages,
            threadId: historyData?.thread_id || prev.threadId,
            loading: false
          }));
          return;
        }
      }
      
      console.log('ChatPanelProvider: No conversation found after refresh, clearing chat');
      setChatPanelState(prev => ({
        ...prev,
        conversation: [],
        loading: false
      }));
    } catch (error) {
      console.error('ChatPanelProvider: Failed to refresh conversation:', error);
      setChatPanelState(prev => ({
        ...prev,
        loading: false
      }));
    }
  }, [authState.isInitialized, authState.isAuthenticated, mapCheckpointMessages]);

  const contextValue = {
    // State
    chatPanelState,
    
    // Actions  
    showChatPanel,
    hideChatPanel,
    toggleMinimizeChat,
    restoreChatPanel,
    navigateWithChat,
    returnToChat,
    updateConversation,
    setLoading,
    setAiThinking,
    setVisitorTokenLimitReached,
    clearConversation,
    resizeChatPanel,
    refreshConversation,
    
    // Computed
    shouldShowChatPanel,
    
    // Utils
    isCurrentlyInMultiModal: shouldShowChatPanel() && location.pathname !== '/chat',
  };

  return (
    <ChatPanelContext.Provider value={contextValue}>
      {children}
    </ChatPanelContext.Provider>
  );
};

export default ChatPanelContext;
